import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getHelp, updateHelp } from '../services/help'
import { visibleElement } from './dom'

const HelpContext = createContext(null)

const FLUSH_MS = 800

function toState(help) {
  return {
    seen: new Set(help?.seen || []),
    steps: new Set(help?.steps || []),
    off: Boolean(help?.off),
  }
}

function addTo(state, key, id) {
  if (!state || state[key].has(id)) return state
  const next = new Set(state[key])
  next.add(id)
  return { ...state, [key]: next }
}

// Saved help progress for the signed-in user, plus the one tour or tip on
// screen. Only one thing is shown at a time; `lastClosed` lets the runner wait
// a while before offering the next tip.
export function HelpProvider({ children }) {
  const [state, setState] = useState(null)
  const [tour, setTour] = useState(null)
  const [tip, setTip] = useState(null)
  const lastClosed = useRef(0)
  const pending = useRef({ seen: new Set(), steps: new Set() })
  const timer = useRef(null)

  const refresh = useCallback(
    () =>
      getHelp()
        .then((help) => setState(toState(help)))
        // Without saved progress, stay quiet rather than repeat every tour.
        .catch(() => setState((current) => current || { ...toState(null), off: true })),
    []
  )

  useEffect(() => {
    refresh()
  }, [refresh])

  const flush = useCallback(() => {
    timer.current = null
    const seen = [...pending.current.seen]
    const steps = [...pending.current.steps]
    pending.current = { seen: new Set(), steps: new Set() }
    if (seen.length || steps.length) updateHelp({ seen, steps }).catch(() => {})
  }, [])

  useEffect(() => () => {
    if (timer.current) {
      clearTimeout(timer.current)
      flush()
    }
  }, [flush])

  const queue = useCallback(
    (key, id) => {
      pending.current[key].add(id)
      if (!timer.current) timer.current = setTimeout(flush, FLUSH_MS)
    },
    [flush]
  )

  const markSeen = useCallback(
    (id) => {
      setState((current) => addTo(current, 'seen', id))
      queue('seen', id)
    },
    [queue]
  )

  const markStep = useCallback(
    (id) => {
      setState((current) => addTo(current, 'steps', id))
      queue('steps', id)
    },
    [queue]
  )

  const setOff = useCallback((off) => {
    setState((current) => (current ? { ...current, off } : current))
    if (off) {
      setTour(null)
      setTip(null)
    }
    updateHelp({ off }).catch(() => {})
  }, [])

  const reset = useCallback(() => {
    setTour(null)
    setTip(null)
    lastClosed.current = 0
    return updateHelp({ reset: true }).then((help) => setState(toState(help)))
  }, [])

  // Steps whose target is not on screen are dropped when the tour starts.
  const startTour = useCallback((def, route) => {
    const steps = def.steps.filter((step) => !step.target || visibleElement(step.target))
    if (!steps.length) return false
    setTip(null)
    setTour({ id: def.id, steps, index: 0, route })
    return true
  }, [])

  const tourRef = useRef(null)
  const tipRef = useRef(null)
  tourRef.current = tour
  tipRef.current = tip

  const endTour = useCallback(
    (completed = true) => {
      if (tourRef.current && completed) markSeen(tourRef.current.id)
      setTour(null)
      lastClosed.current = Date.now()
    },
    [markSeen]
  )

  const moveTour = useCallback((delta) => {
    setTour((current) => {
      if (!current) return current
      const index = Math.min(Math.max(current.index + delta, 0), current.steps.length - 1)
      return { ...current, index }
    })
  }, [])

  const closeTip = useCallback(
    (seen = true) => {
      if (tipRef.current && seen) markSeen(tipRef.current.id)
      setTip(null)
      lastClosed.current = Date.now()
    },
    [markSeen]
  )

  const value = useMemo(
    () => ({
      loaded: Boolean(state),
      seen: state?.seen || new Set(),
      steps: state?.steps || new Set(),
      off: Boolean(state?.off),
      tour,
      tip,
      lastClosed,
      refresh,
      markSeen,
      markStep,
      setOff,
      reset,
      startTour,
      endTour,
      moveTour,
      showTip: setTip,
      closeTip,
    }),
    [state, tour, tip, refresh, markSeen, markStep, setOff, reset, startTour, endTour, moveTour, closeTip]
  )

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>
}

export function useHelp() {
  const value = useContext(HelpContext)
  if (!value) throw new Error('useHelp must be used inside HelpProvider')
  return value
}
